import { AnalyticsType } from "@src/appV2/Analytics/constants";
import { getLocation, routes } from "@src/appV2/redesign/utils/routes";

import { type WorkplaceMenuPrivileges } from "../privilegesProviders/WorkplaceMenuPrivilegesProvider";
import { type NavigationItem, type NavigationMenu, type NavigationSection } from "./types";
import { getRouteAndSelected, isPathSelected } from "./utils";

interface AnalyticsConfig {
  type: AnalyticsType;
  embedId: string;
}

interface WorkplaceNavigationOptions {
  unreadChannelCount?: number;
  pendingExtraWorkedTimeRequestCount?: number;
  isMobile?: boolean;
  isMonthlyViewEnabled?: boolean;
  isClassicCalendarPathActive?: boolean;
}

export function getWorkplaceNavigationMenu(
  workplaceMenuPrivileges: WorkplaceMenuPrivileges,
  currentPath: string,
  analyticsConfig: AnalyticsConfig[] = [],
  options: WorkplaceNavigationOptions = {}
): NavigationMenu {
  const {
    unreadChannelCount,
    pendingExtraWorkedTimeRequestCount,
    isMobile,
    isMonthlyViewEnabled,
    isClassicCalendarPathActive,
  } = options;
  const items: Array<NavigationItem | NavigationSection> = [];

  // Home Health specific items (shown at top for Home Health workplaces)
  if (workplaceMenuPrivileges.homeHealthSection) {
    if (workplaceMenuPrivileges.homeHealthSection.cases) {
      items.push({
        label: "Cases",
        iconType: "profile",
        ...getRouteAndSelected("homeHealthCases", currentPath),
      });
    }
    if (workplaceMenuPrivileges.homeHealthSection.visitVerification) {
      items.push({
        label: "Visit Verification",
        iconType: "badge-check",
        ...getRouteAndSelected("homeHealthVisitVerification", currentPath),
      });
    }
    if (workplaceMenuPrivileges.homeHealthSection.coverageMap) {
      items.push({
        label: "Coverage Map",
        iconType: "location-alt",
        ...getRouteAndSelected("homeHealthCoverageMap", currentPath),
      });
    }
  }

  if (workplaceMenuPrivileges.workplaceOnboarding) {
    items.push({
      label: "Getting started",
      iconType: "hybrid-view",
      ...getRouteAndSelected("facilityOnboarding", currentPath),
    });
  }

  if (workplaceMenuPrivileges.dailyView) {
    const useMonthlyDefault = !isMobile && isMonthlyViewEnabled;
    items.push({
      label: "Schedule",
      iconType: "calendar-day",
      routeTo: useMonthlyDefault
        ? { pathname: routes.schedule, search: "?view=month" }
        : { pathname: routes.schedule },
      isSelected: isPathSelected(currentPath, routes.schedule),
    });
  }

  if (!isMobile && workplaceMenuPrivileges.calendar) {
    // While the /facility/dashboard → /schedule redirect is active, the legacy
    // classic calendar lives at /calendar-classic. For workplaces without the
    // redirect, the legacy view still renders at /facility/dashboard, so point
    // the sidebar at the path that will actually render to keep the item's
    // selected state in sync with the URL.
    items.push({
      label: "Calendar (Classic)",
      iconType: "calendar",
      ...getRouteAndSelected(
        isClassicCalendarPathActive ? "calendarClassic" : "facilityDashboard",
        currentPath
      ),
    });
  }

  if (workplaceMenuPrivileges.chat) {
    const chatRouteKey = workplaceMenuPrivileges.chatRedesign ? "chat" : "facilityChat";
    items.push({
      label: "Chat",
      iconType: "message",
      ...getRouteAndSelected(chatRouteKey, currentPath),
      badgeCount: unreadChannelCount,
    });
  }

  if (workplaceMenuPrivileges.professionalsSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.professionalsSection.myProfessionals) {
      sectionItems.push({
        label: "My Professionals",
        iconType: "folder-person",
        ...getRouteAndSelected("workplaceWorkers", currentPath),
      });
    }
    if (!isMobile && workplaceMenuPrivileges.professionalsSection.browseProfessionals) {
      sectionItems.push({
        label: "Browse Professionals",
        iconType: "people",
        ...getRouteAndSelected("workersDirectory", currentPath),
      });
    }

    items.push({ label: "Professionals", items: sectionItems });
  }

  if (
    !isMobile &&
    workplaceMenuPrivileges.permanentHiringSection &&
    !workplaceMenuPrivileges.isPermanentHiringSectionLoading
  ) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.permanentHiringSection.placementsLandingPage) {
      sectionItems.push({
        label: "Landing Page",
        iconType: "ribbon",
        ...getRouteAndSelected("jobsLandingPage", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.interviewSchedule) {
      sectionItems.push({
        label: "Interview Schedule",
        iconType: "clock-outlined",
        ...getRouteAndSelected("interviewSchedule", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.applicationAndJobs) {
      sectionItems.push({
        label: "Application & Jobs",
        iconType: "briefcase",
        ...getRouteAndSelected("manageJobs", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.browseTalentPool) {
      sectionItems.push({
        label: "Browse Talent Pool",
        iconType: "person-superstar",
        ...getRouteAndSelected("workplaceHireProfessionals", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.interviewAvailability) {
      sectionItems.push({
        label: "Interview Availability",
        iconType: "person-male-formal",
        ...getRouteAndSelected("interviewSettings", currentPath),
      });
    }

    items.push({ label: "Permanent Hiring", items: sectionItems });
  }

  if (workplaceMenuPrivileges.charting) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Charting",
      iconType: "chart-line-increase",
      ...getRouteAndSelected("workplaceCharting", currentPath),
    });

    items.push({ label: "Charting", items: sectionItems });
  }

  if (workplaceMenuPrivileges.analyticsSection) {
    const sectionItems: NavigationItem[] = [];
    const workplaceAnalyticsPath = "/workplace/analytics";

    const workplaceAnalyticsEmbedId = analyticsConfig.find(
      (config) => config.type === AnalyticsType.WORKPLACE_FLEX
    )?.embedId;

    const corporateAnalyticsEmbedId = analyticsConfig.find(
      (config) => config.type === AnalyticsType.CORPORATE_FLEX
    )?.embedId;

    if (workplaceMenuPrivileges.analyticsSection.workplaceAnalytics && workplaceAnalyticsEmbedId) {
      sectionItems.push({
        label: "Workplace Analytics",
        iconType: "microscope",
        routeTo: getLocation("workplaceAnalytics", {
          queryParams: { embedId: workplaceAnalyticsEmbedId },
        }),
        isSelected: currentPath.includes(workplaceAnalyticsPath),
      });
    }

    // Only show corporate analytics in Analytics section when Corporate Center is not visible
    if (
      !workplaceMenuPrivileges.corporateCenterSection &&
      workplaceMenuPrivileges.analyticsSection.corporateAnalytics &&
      corporateAnalyticsEmbedId
    ) {
      sectionItems.push({
        label: "Corporate Analytics",
        iconType: "binoculars",
        routeTo: getLocation("workplaceAnalytics", {
          queryParams: { embedId: corporateAnalyticsEmbedId },
        }),
        isSelected: currentPath.includes(workplaceAnalyticsPath),
      });
    }

    if (sectionItems.length > 0) {
      items.push({ label: "Analytics", items: sectionItems });
    }
  }

  if (workplaceMenuPrivileges.corporateCenterSection) {
    const sectionItems: NavigationItem[] = [];
    const workplaceAnalyticsPath = "/workplace/analytics";
    const spendingControlsPath = "/corporate/spending-controls";

    const corporateAnalyticsEmbedId = analyticsConfig.find(
      (config) => config.type === AnalyticsType.CORPORATE_FLEX
    )?.embedId;

    if (
      workplaceMenuPrivileges.corporateCenterSection.corporateAnalytics &&
      corporateAnalyticsEmbedId
    ) {
      sectionItems.push({
        label: "Corporate Analytics",
        iconType: "binoculars",
        routeTo: getLocation("workplaceAnalytics", {
          queryParams: { embedId: corporateAnalyticsEmbedId },
        }),
        isSelected: currentPath.includes(workplaceAnalyticsPath),
      });
    }

    if (workplaceMenuPrivileges.corporateCenterSection.spendingControls) {
      sectionItems.push({
        label: "Spend Controls",
        iconType: "gauge",
        routeTo: getLocation("corporateSpendingControls"),
        isSelected: currentPath.includes(spendingControlsPath),
      });
    }

    if (sectionItems.length > 0) {
      items.push({ label: "Corporate Center", items: sectionItems });
    }
  }

  if (workplaceMenuPrivileges.liveWorkerETA) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Live Worker ETA",
      iconType: "coordinates-person",
      ...getRouteAndSelected("facilityLiveWorkerETA", currentPath),
    });

    items.push({ label: "Live Worker ETA", items: sectionItems });
  }

  if (!isMobile && workplaceMenuPrivileges.workplaceRulesQuiz) {
    items.push({
      label: "Workplace Rules Quiz",
      iconType: "book",
      routeTo: getLocation("facilityWorkerInstructions", {
        queryParams: { click_src: "menu_item" },
      }),
      isSelected: currentPath.includes("/facility/worker-instructions"),
    });
  }

  if (workplaceMenuPrivileges.managementSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.managementSection.myAccount) {
      sectionItems.push({
        label: "My Account",
        iconType: "person-male",
        ...getRouteAndSelected("myAccount", currentPath),
      });
    }

    if (!isMobile && workplaceMenuPrivileges.managementSection.teamMembers) {
      sectionItems.push({
        label: "Team Members",
        iconType: "group",
        ...getRouteAndSelected("workplaceUsers", currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.requiredDocuments) {
      sectionItems.push({
        label: "Workplace Requirements",
        iconType: "documents",
        ...getRouteAndSelected("workplaceDocumentMapping", currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.workplaceInformation) {
      sectionItems.push({
        label: "Workplace Information",
        iconType: "information",
        ...getRouteAndSelected("facilityWorkerInstructions", currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.settings) {
      const settingsRouteKey = workplaceMenuPrivileges.settingsRedesign
        ? "settings"
        : "facilitySettings";
      sectionItems.push({
        label: "Settings",
        iconType: "cogwheel",
        ...getRouteAndSelected(settingsRouteKey, currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.timeclockingSettings) {
      sectionItems.push({
        label: "Timeclocking Settings",
        iconType: "cogwheel",
        ...getRouteAndSelected("facilityTimeclockSettings", currentPath),
      });
    }

    items.push({ label: "Management", items: sectionItems });
  }

  if (workplaceMenuPrivileges.billingSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.billingSection.reporting) {
      sectionItems.push({
        label: "Reporting",
        iconType: "chart-line-increase",
        ...getRouteAndSelected("facilityInvoiceReporting", currentPath),
      });
    }

    if (workplaceMenuPrivileges.billingSection.upcomingCharges) {
      sectionItems.push({
        label: "Upcoming Charges",
        iconType: "dollar",
        ...getRouteAndSelected("facilityInvoiceUpcomingCharges", currentPath),
        badgeCount: pendingExtraWorkedTimeRequestCount,
      });
    }

    if (workplaceMenuPrivileges.billingSection.workplaceSpendingControls) {
      sectionItems.push({
        label: "Spend Controls",
        iconType: "gauge",
        ...getRouteAndSelected("workplaceSpendingControls", currentPath),
      });
    }

    if (workplaceMenuPrivileges.billingSection.invoiceHistory) {
      sectionItems.push({
        label: "Invoice History",
        iconType: "money-dollar",
        ...getRouteAndSelected("facilityInvoiceHistory", currentPath),
      });
    }

    if (!isMobile && workplaceMenuPrivileges.billingSection.contractTerms) {
      sectionItems.push({
        label: "Contract Terms",
        iconType: "notepad-alt",
        ...getRouteAndSelected("facilityInvoiceContractTerms", currentPath),
      });
    }

    items.push({ label: "Billing", items: sectionItems });
  }

  if (workplaceMenuPrivileges.helpCenter) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Help Center",
      iconType: "question-circle",
      ...getRouteAndSelected("helpCenter", currentPath),
    });

    items.push({ label: "Help Center", items: sectionItems });
  }

  if (workplaceMenuPrivileges.referAndEarn) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Refer & Earn",
      iconType: "palm-dollar",
      ...getRouteAndSelected("workplaceReferAndEarn", currentPath),
    });

    items.push({ label: "Refer & Earn", items: sectionItems });
  }

  return { items };
}
